import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { SEARCH_ASSETS } from '../../http/apollo-queries';
import { Icon, Loading } from '../UI';
import { IAsset, TAssetType } from '../../types/model';
import { useTranslation } from 'react-i18next';
import { Asset } from '../Asset/Asset';
import { useTimeout } from '../../hooks/useTimeout';

interface ISearchAssetProps {
  onChoose(p: IAsset): any;

  onDelete?(p: HTMLInputElement): any;

  defaultSelected?: IAsset;
  title?: string;
  color?: string;
  isDeletable?: boolean;
  isMultiple?: boolean;
  isDisabled?: boolean;
  types?: (TAssetType | 'ALL')[];
}

const SearchAsset = ({
  color = 'purple',
  defaultSelected,
  title,
  onChoose,
  types = ['ALL', 'STOCK', 'CRYPTO'],
  isDeletable = false,
  isMultiple = false,
  isDisabled = false,
  onDelete,
}: ISearchAssetProps) => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [chosenType, setChosenType] = useState<TAssetType | 'ALL'>(
    types?.length === 1 ? types[0] : 'ALL'
  );
  const [selected, setSelected] = useState<IAsset | undefined>(defaultSelected);
  const [assetList, setAssetList] = useState<any[]>([]);
  const [actualSearch, setActualSearch] = useState<string>('*');

  const [searchAssets, { loading }] = useLazyQuery(SEARCH_ASSETS);

  useEffect(() => {
    getAssetsByType();
  }, [chosenType, actualSearch]);

  const setOpen = () => {
    setIsOpen(!isOpen);
  };

  const getAssetsByType = () => {
    searchAssets({
      variables: {
        term: actualSearch,
        type: chosenType !== 'ALL' ? chosenType : undefined,
      },
    }).then(({ data: { searchAssets: { items = [] } = {} } = {} }) => {
      setAssetList(items);
    });
  };

  const handleKeyUp: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.code === 'Enter') {
      setActualSearch(e.currentTarget.value);
    }
  };

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setValue(e.currentTarget.value);
  };

  const handleDelete: React.MouseEventHandler<HTMLInputElement> = (e) => {
    setActualSearch('');
    setSelected(undefined);
    setIsOpen(false);
    onDelete?.(e.currentTarget);
  };

  // Search with debounce
  const { value, setValue } = useTimeout(() => {
    if (!value) {
      setActualSearch('');
      return;
    }

    setActualSearch(value);
  }, 500);

  return (
    <>
      <div className={'relative z-90'}>
        <div className={'flex mx-2'}>
          <div
            className={`${
              isDisabled
                ? 'bg-grey-10 text-grey-30 cursor-not-allowed pointer-events-none dark:bg-grey-80 dark:text-grey-70'
                : ''
            }
              border-1 border-grey-10 rounded-2xl p-2 flex items-center text-xxs uppercase font-semibold cursor-pointer uppercase dark:border-grey-80`}
          >
            <div className={'flex items-center'} onClick={setOpen}>
              <div className={`bg-${color} w-2 h-2 rounded-full mx-2`} />
              {selected && selected.name && !isMultiple
                ? selected.name
                : title || t('widget.chooseAsset')}

              <Icon className={'ml-2'} svgName={'arrowDown'} />
            </div>
            {isDeletable && selected && (
              <Icon
                onClick={handleDelete}
                className={'ml-2 w-2'}
                svgName={'close'}
              />
            )}
          </div>
        </div>

        {isOpen && (
          <div
            className={
              'absolute bg-white top-12 left-2 z-90 rounded-2xl border-1 border-grey-30 w-96 dark:bg-grey-90 dark:border-grey-80 dark:text-white'
            }
          >
            {loading && (
              <div className="flex rounded-md absolute w-full h-full z-100 text-center bg-white dark:bg-black bg-black dark:text-white top-0 left-0">
                <Loading
                  status={true}
                  size={'large'}
                  className={'text-center m-auto'}
                />
              </div>
            )}

            <div className={'flex items-center w-full m-4'}>
              <Icon svgName={'search'} />
              <input
                className={'w-10/12 ml-1 pr-2 bg-transparent'}
                placeholder={'search asset'}
                type={'text'}
                onChange={handleChange}
                onKeyUp={handleKeyUp}
              />
            </div>

            <div
              className={
                'flex items-center px-2 text-sm w-full mt-3 text-center border-grey-10 border-t-1 border-b-1 py-2 justify-start dark:border-grey-80'
              }
            >
              {types.map((type) => (
                <div
                  className={`flex cursor-pointer justify-center mx-3 hover:text-black ${
                    chosenType === type
                      ? 'text-black dark:text-white'
                      : 'text-grey-50'
                  }`}
                  onClick={() => setChosenType(type)}
                >
                  {type}
                </div>
              ))}
            </div>

            <div className={'w-full h-48 overflow-y-auto overflow-x-hidden'}>
              {assetList.map((asset, i) => (
                <div
                  key={i}
                  className={
                    'flex w-full p-1 items-center text-sm cursor-pointer'
                  }
                  onClick={() => {
                    setSelected(asset);
                    setOpen();
                    onChoose(asset);
                  }}
                >
                  <div className={'rounded-lg hover:bg-lime w-full px-2 py-1'}>
                    <Asset
                      connectionType={''}
                      id={asset.id}
                      type={asset.type}
                      name={asset.name}
                      symbol={asset.symbol}
                      logo={asset.logo}
                      isClickable={false}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SearchAsset;
